import { supabase } from "../supabase";
import { getDefaultOrganizationId } from "./organizationHelpers";

export const MEMBERSHIP_ROLE = Object.freeze({
  OWNER: "owner",
  ADMIN: "admin",
  MANAGER: "manager",
  EMPLOYEE: "employee",
  GUEST: "guest",
});

const CURRENT_MEMBERSHIP_ORG_KEY = "CP_CURRENT_MEMBERSHIP_ORG_ID";

// organization_members relates to organizations through two foreign keys
  // (organization_id, and organizations.owner_member_id). An organizations()
  // embed is ambiguous to PostgREST (HTTP 300), the membership load returns
  // nothing, and the signed-in actor loses organization_id.
const MEMBER_SELECT = `
  id,
  organization_id,
  profile_id,
  role,
  employee_name,
  email,
  phone,
  user_code,
  manager_member_id,
  active,
  sms_enabled,
  hourly_rate,
  payroll_type,
  classification_name,
  payroll_access,
  finance_access,
  default_project_id,
  trade_id,
  trades ( trade_name ),
  hire_date,
  emergency_contact,
  emergency_phone,
  device_label,
  notes,
  last_login,
  joined_at,
  created_at,
  updated_at,
  profiles ( supabase_user_id, display_name, is_system_owner )
`;

export function loadStoredMembershipOrgId() {
  try {
    return localStorage.getItem(CURRENT_MEMBERSHIP_ORG_KEY);
  } catch {
    return null;
  }
}

export function persistStoredMembershipOrgId(organizationId) {
  if (!organizationId) {
    return;
  }
  try {
    localStorage.setItem(CURRENT_MEMBERSHIP_ORG_KEY, organizationId);
  } catch {
    // ignore
  }
}

export async function loadProfileByAuthUserId(authUserId, client = supabase) {
  if (!authUserId) {
    return null;
  }

  const { data, error } = await client
    .from("profiles")
    .select("id, supabase_user_id, display_name, avatar_url, is_system_owner, created_at, updated_at")
    .eq("supabase_user_id", authUserId)
    .maybeSingle();

  if (error) {
    console.warn("[membershipHelpers] profile load failed", error);
    return null;
  }

  return data;
}

export async function loadActiveMembershipsForProfile(profileId, client = supabase) {
  if (!profileId) {
    return [];
  }

  const { data, error } = await client
    .from("organization_members")
    .select(MEMBER_SELECT)
    .eq("profile_id", profileId)
    .eq("active", true)
    .order("joined_at", { ascending: true });

  if (error) {
    console.warn("[membershipHelpers] memberships load failed", error);
    return [];
  }

  return data || [];
}

/**
 * Pure resolution: pick current membership from active list.
 * Priority: stored org preference → default org pointer → first membership.
 */
export function resolveCurrentMembership(
  memberships,
  { storedOrgId = null, defaultOrgId = null } = {},
) {
  if (!memberships?.length) {
    return null;
  }

  return (
    memberships.find((row) => storedOrgId && row.organization_id === storedOrgId) ||
    memberships.find((row) => defaultOrgId && row.organization_id === defaultOrgId) ||
    memberships[0] ||
    null
  );
}

/**
 * Auth user ids for active members of an organization (Gate 5 user-table scoping).
 */
export async function loadOrganizationMemberAuthUserIds(
  organizationId,
  client = supabase,
) {
  if (!organizationId) {
    return [];
  }

  const { data, error } = await client
    .from("organization_members")
    .select("profiles ( supabase_user_id )")
    .eq("organization_id", organizationId)
    .eq("active", true);

  if (error) {
    throw error;
  }

  return (data || [])
    .map((row) => row.profiles?.supabase_user_id)
    .filter(Boolean);
}

export async function loadManagerSupabaseUserId(managerMemberId, client = supabase) {
  if (!managerMemberId) {
    return null;
  }

  const { data, error } = await client
    .from("organization_members")
    .select("profiles ( supabase_user_id )")
    .eq("id", managerMemberId)
    .maybeSingle();

  if (error) {
    console.warn("[membershipHelpers] manager lookup failed", error);
    return null;
  }

  return data?.profiles?.supabase_user_id ?? null;
}

export async function loadMembershipForAuthUser(authUserId, client = supabase) {
  const profile = await loadProfileByAuthUserId(authUserId, client);
  if (!profile) {
    return {
      profile: null,
      memberships: [],
      membership: null,
      managerSupabaseUserId: null,
    };
  }

  const memberships = await loadActiveMembershipsForProfile(profile.id, client);
  const storedOrgId = loadStoredMembershipOrgId();
  const defaultOrgId = await getDefaultOrganizationId(client);

  const membership = resolveCurrentMembership(memberships, {
    storedOrgId,
    defaultOrgId,
  });

  if (membership?.organization_id) {
    persistStoredMembershipOrgId(membership.organization_id);
  }

  let managerSupabaseUserId = null;
  if (membership?.manager_member_id) {
    managerSupabaseUserId = await loadManagerSupabaseUserId(
      membership.manager_member_id,
      client,
    );
  }

  return { profile, memberships, membership, managerSupabaseUserId };
}

export function mapMembershipRoleToLegacyRole(membershipRole) {
  switch (membershipRole) {
    case MEMBERSHIP_ROLE.OWNER:
    case MEMBERSHIP_ROLE.ADMIN:
      return "admin";
    case MEMBERSHIP_ROLE.MANAGER:
      return "manager";
    case MEMBERSHIP_ROLE.GUEST:
      return "guest";
    default:
      return "employee";
  }
}

export function isMembershipOwner(membership) {
  return membership?.role === MEMBERSHIP_ROLE.OWNER;
}

export function isMembershipAdmin(membership) {
  return (
    membership?.role === MEMBERSHIP_ROLE.OWNER ||
    membership?.role === MEMBERSHIP_ROLE.ADMIN
  );
}

export function isMembershipManager(membership) {
  return membership?.role === MEMBERSHIP_ROLE.MANAGER || isMembershipAdmin(membership);
}
